import {
  ADMIN_LOCATIONS_DETAILS,
  ADMIN_RECEIVABLES_LOCATION,
  AdminHeader,
  AdminSpinner,
  Button,
  DebtRepaymentService,
  Div,
  H5,
  OutstandingItem,
  P,
  ReceivablesDebtRepayment,
  TableHeader,
  UserRole,
  useAuth,
  useIsMobile,
} from '@vgrubs/components';
// Direct import: this one is deliberately outside the barrel.
import { NoDataComponent } from 'components/src/adminComponents/NoDataComponent/NoDataComponent';
import { useState } from 'react';
import { useMatch, useNavigate } from 'react-router';
import { BalanceOverview } from './components/BalanceOverview';
import { CallSummaryModal } from './components/CallSummaryModal';
import { MerchantView } from './components/MerchantView';
import {
  ITEM_COLUMNS,
  OutstandingItemRow,
} from './components/OutstandingItemRow';
import { RepaymentPlanSection } from './components/RepaymentPlanSection';
import { planService } from './helpers/repaymentPlanLabels';
import { ResolveItemModal, ResolveMode } from './components/ResolveItemModal';
import { RetryPaymentModal } from './components/RetryPaymentModal';
import { useCollectionLetter } from './hooks/useCollectionLetter';
import { useCollectionLetterSummary } from './hooks/useCollectionLetterSummary';
import { useReceivablesActions } from './hooks/useReceivablesActions';
import {
  useReceivablesLocation,
  useReceivablesLocationName,
} from './hooks/useReceivablesLocation';

/**
 * The plan cards a location shows: vDrive always (its card says when there is
 * nothing to repay), SaaS once there is something to repay or a plan to show.
 * A backend from before Oct 22, 2026 knows vDrive plans only.
 */
const planServicesFor = (
  debtRepayment: ReceivablesDebtRepayment
): DebtRepaymentService[] => {
  const hasSaas =
    (debtRepayment.saasRepayableDebt ?? 0) > 0 ||
    debtRepayment.plans.some((plan) => planService(plan) === 'saas');
  return hasSaas ? ['vDrive', 'saas'] : ['vDrive'];
};

/**
 * One location's receivables: where it stands now, what the merchant is
 * shown, and every item it ever had - with the admin actions on them.
 */
export const ReceivablesLocation = () => {
  const navigate = useNavigate();
  const locationId = Number(
    useMatch(ADMIN_RECEIVABLES_LOCATION)?.params.locationId
  );
  const { detail, isLoading } = useReceivablesLocation(locationId);
  const debtRepayment = detail?.debtRepayment;
  const { name, ownerName } = useReceivablesLocationName(locationId);
  const role = useAuth((state) => state.user?.role);
  const isAdmin = role === UserRole.admin;
  const actions = useReceivablesActions();
  const collectionLetter = useCollectionLetter(locationId);
  // Only a deleted location that still owes gets a letter or a call summary.
  const owesAsDeleted = !!detail?.deleted && detail.current.total > 0;
  // The letter is admin only (support would get 403); the summary is not.
  const canDownloadLetter = isAdmin && owesAsDeleted;
  const canSeeSummary = (isAdmin || role === UserRole.support) && owesAsDeleted;
  // From the click until the modal closes; the modal opens once it loads.
  const [isSummaryRequested, setIsSummaryRequested] = useState(false);
  const closeSummary = () => setIsSummaryRequested(false);
  const callSummary = useCollectionLetterSummary(
    locationId,
    isSummaryRequested,
    closeSummary
  );
  // The phone header has no room for more buttons, so there they open the page.
  const isMobile = useIsMobile();
  const letterButtons = (canDownloadLetter || canSeeSummary) && (
    <Div $flex $gap="12px" $columnSM>
      {canSeeSummary && (
        <Button
          $small
          // Wide enough for the label on one line and the spinner beside it.
          $w="170px"
          $wSM="100%"
          $gray
          $selected
          // $loading already swallows clicks; `disabled` would grey the spinner out.
          $loading={callSummary.isLoading}
          onClick={() => setIsSummaryRequested(true)}
        >
          Call summary
        </Button>
      )}
      {canDownloadLetter && (
        <Button
          $small
          // Wide enough for the label on one line and the spinner beside it.
          $w="230px"
          $wSM="100%"
          $selected
          // $loading already swallows clicks; `disabled` would grey the spinner out.
          $loading={collectionLetter.isPending}
          onClick={collectionLetter.open}
        >
          Collection letter (PDF)
        </Button>
      )}
    </Div>
  );

  const [resolving, setResolving] = useState<{
    item: OutstandingItem;
    mode: ResolveMode;
  } | null>(null);
  const [retrying, setRetrying] = useState<OutstandingItem | null>(null);

  // The location name opens its Location Details in a new tab, so this page
  // stays where it was; the owner stays plain.
  const subtitle = name ? (
    <>
      {Number.isFinite(locationId) ? (
        <P
          as="a"
          href={ADMIN_LOCATIONS_DETAILS.replace(':id', `${locationId}`)}
          target="_blank"
          rel="noopener noreferrer"
          $m="0"
          $pointer
          $hoverUnderline
          style={{ font: 'inherit', color: 'inherit', textDecoration: 'none' }}
        >
          {name}
        </P>
      ) : (
        name
      )}
      {ownerName && ` - ${ownerName}`}
    </>
  ) : (
    ownerName
  );

  return (
    <>
      <AdminHeader
        title="Outstanding Balances"
        subtitle={subtitle || undefined}
      >
        <Div $flex $gap="12px">
          {!isMobile && letterButtons}
          {detail?.current.vDrive && (
            <Button
              $small
              $gray
              $selected
              onClick={() => navigate(`/locations/v-drive/${locationId}`)}
            >
              Manage vDrive
            </Button>
          )}
        </Div>
      </AdminHeader>

      {isLoading && <AdminSpinner />}

      {detail && (
        <Div $flex $column $gap="24px" $m="32px 0" $mSM="20px 0">
          {isMobile && letterButtons}
          <Div $flex $gap="24px" $columnSM $alignStart>
            <Div $width="50%" $widthSM="100%">
              <BalanceOverview current={detail.current} />
            </Div>
            <Div $width="50%" $widthSM="100%">
              <MerchantView current={detail.current} />
            </Div>
          </Div>

          {debtRepayment &&
            planServicesFor(debtRepayment).map((service) => (
              <RepaymentPlanSection
                key={service}
                locationId={locationId}
                service={service}
                debtRepayment={debtRepayment}
                canEdit={isAdmin}
                busy={actions.busy}
                onSave={(plan, data) =>
                  plan
                    ? actions.updatePlan(locationId, plan.id, data)
                    : actions.createPlan(locationId, data)
                }
                onStop={(plan, note) =>
                  actions.cancelPlan(locationId, plan.id, { note })
                }
              />
            ))}

          <Div>
            <H5 $mb="16">History</H5>
            {detail.history.length > 0 ? (
              <>
                <TableHeader columns={ITEM_COLUMNS} gap="24px" />
                {detail.history.map((item) => (
                  <OutstandingItemRow
                    key={item.id}
                    item={item}
                    canResolve={isAdmin}
                    onMarkPaid={(picked) =>
                      setResolving({ item: picked, mode: 'paid' })
                    }
                    onWriteOff={(picked) =>
                      setResolving({ item: picked, mode: 'writeOff' })
                    }
                    onRetry={setRetrying}
                  />
                ))}
              </>
            ) : (
              <NoDataComponent
                height="20vh"
                heightSM="20vh"
                title="No charges"
                description="No charges have been recorded for this location yet."
              />
            )}
          </Div>
        </Div>
      )}

      <ResolveItemModal
        item={resolving?.item ?? null}
        mode={resolving?.mode ?? 'paid'}
        busy={actions.busy}
        onClose={() => setResolving(null)}
        onConfirm={(note, adminPassword) => {
          if (!resolving) return Promise.resolve(false);
          const resolve =
            resolving.mode === 'paid' ? actions.markPaid : actions.writeOff;
          return resolve(locationId, resolving.item.id, {
            note,
            adminPassword,
          });
        }}
      />

      {canSeeSummary && (
        <CallSummaryModal
          summary={callSummary.summary}
          onClose={closeSummary}
        />
      )}

      <RetryPaymentModal
        item={retrying}
        busy={actions.busy}
        onClose={() => setRetrying(null)}
        onConfirm={() =>
          retrying?.clientPaymentId
            ? actions.retryBankPayment(locationId, retrying.clientPaymentId)
            : Promise.resolve(false)
        }
      />
    </>
  );
};
